import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useGetFunctionDefinitionSubjectAttributeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { useGetSubjectDetails } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { DISPLAY_TABLE_QUERY_KEY } from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { dayjs } from 'utils/DateUtils';
import { ENV_CONFIG } from 'config/env';
import {
  CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY,
  CubeToSubjectDefinition,
  CubeToSubjectRun,
  useGetCubeToSubjectDefinitions,
  useGetCubeToSubjectRun,
  useTriggerCubeToSubjectDefinition,
  isRunTerminal,
  isRunFailed,
} from 'services/v1/Tenant/AROCube/CubeToSubjectDefinitionService';
import { ModalDataFlowDefinition } from './ModalDataFlowDefinition';

interface ModalDataFlowRunnerProps {
  open: boolean;
  onClose: () => void;
  targetSubjectId?: string;
}

// Friendly wording for the cube time interval codes.
const INTERVAL_FRIENDLY: Record<string, string> = {
  BASE: 'base',
  YEARLY: 'yearly',
  HALF_YEARLY: 'half-yearly',
  QUARTERLY: 'quarterly',
  MONTHLY: 'monthly',
  WEEKLY: 'weekly',
  DAILY: 'daily',
};

// Bold highlight for a value inside the plain-English description.
function Token(props: { children: React.ReactNode }) {
  return (
    <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
      {props.children}
    </Box>
  );
}

interface DataFlowRunnerRowProps {
  definition: CubeToSubjectDefinition;
  targetSubjectLabel: string;
  columnLabel: string;
  isTriggering: boolean;
  runDisabled: boolean;
  run?: CubeToSubjectRun | null;
  triggeredByName?: string;
  onRun: () => void;
}

// Human-readable status line for an in-flight run.
function getRunStatusText(run: CubeToSubjectRun): string {
  if (run.status === 'IN_QUEUE') return 'Queued, starting soon…';
  const count = run.totalMembers ? ` · ${run.processedMembers ?? 0} of ${run.totalMembers}` : '';
  if (run.currentMember?.memberName) return `Processing ${run.currentMember.memberName}…${count}`;
  return `Updating…${count}`;
}

const RUN_STATUS_CHIP: Record<string, { label: string; bgcolor: string; color: string }> = {
  COMPLETED: { label: 'Completed', bgcolor: 'rgba(66, 187, 147, 0.12)', color: '#1B7F45' },
  SUCCESS: { label: 'Completed', bgcolor: 'rgba(66, 187, 147, 0.12)', color: '#1B7F45' },
  FAILED: { label: 'Failed', bgcolor: '#FEECEC', color: '#D92D20' },
  ERROR: { label: 'Failed', bgcolor: '#FEECEC', color: '#D92D20' },
};

function getRunStatusChip(status: string) {
  return (
    RUN_STATUS_CHIP[status] ?? {
      label: status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' '),
      bgcolor: '#F1F5F9',
      color: '#98A2AE',
    }
  );
}

// One data flow row. Source measure, layer and cube names come straight from the
// definition DTO, so no extra lookups are needed.
function DataFlowRunnerRow(props: Readonly<DataFlowRunnerRowProps>) {
  const { definition, targetSubjectLabel, columnLabel, isTriggering, runDisabled, run, triggeredByName, onRun } =
    props;
  const { source } = definition;

  const valueName = source.cubeAttributeName
    ? `${source.cubeLayerName ? `${source.cubeLayerName} ` : ''}${source.cubeAttributeName}`
    : null;
  const intervalFriendly = INTERVAL_FRIENDLY[source.timeInterval];

  const isActive = !!run || isTriggering;
  const percent = run?.progressPercent ?? 0;
  const indeterminate = isTriggering || run?.status === 'IN_QUEUE';

  const latestRun = definition.latestRun;
  const latestChip = latestRun ? getRunStatusChip(latestRun.status) : null;
  const latestWhen =
    latestRun?.triggeredAt || latestRun?.finishedAt
      ? dayjs(latestRun.triggeredAt || latestRun.finishedAt).format(ENV_CONFIG.config.DATE_TIME_FORMAT)
      : null;

  return (
    <Stack
      spacing={1}
      sx={{
        px: 1.5,
        py: 1.25,
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: isActive ? '#42BB93' : '#E3EBF6',
        borderRadius: 1,
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:hover': { borderColor: isActive ? '#42BB93' : '#C8D7EC', boxShadow: '0 1px 4px rgba(16, 24, 40, 0.06)' },
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1.5}>
        <Stack spacing={0.5} flex={1} minWidth={0}>
          <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797' }} noWrap>
            {definition.name}
          </Typography>
          <Typography variant='body2' sx={{ color: '#475467' }}>
            Fills the <Token>{columnLabel}</Token> column of {targetSubjectLabel} with{' '}
            {valueName ? <Token>{valueName}</Token> : 'the latest calculated figure'}
            {source.cubeName ? (
              <>
                {' '}
                from <Token>{source.cubeName}</Token>
              </>
            ) : null}{' '}
            for the <Token>{source.timePeriodValue}</Token>
            {intervalFriendly ? ` ${intervalFriendly}` : ''} period.
          </Typography>
        </Stack>
        <Tooltip title={runDisabled && !isActive ? 'Another update is already in progress' : ''}>
          <span style={{ flexShrink: 0 }}>
            <ButtonLoading
              variant='main-table-panel'
              startIcon={<PlayArrowRoundedIcon />}
              loading={isActive}
              disabled={runDisabled}
              onClick={onRun}
              sx={{ textTransform: 'none' }}
            >
              {isActive ? 'Updating' : 'Update'}
            </ButtonLoading>
          </span>
        </Tooltip>
      </Stack>

      {isActive && (
        <Stack spacing={0.5}>
          <LinearProgress
            variant={indeterminate ? 'indeterminate' : 'determinate'}
            value={percent}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: '#EDF1F7',
              '& .MuiLinearProgress-bar': { bgcolor: '#42BB93' },
            }}
          />
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
            <Typography variant='caption' sx={{ color: '#98A2AE' }} noWrap>
              {run ? getRunStatusText(run) : 'Starting…'}
            </Typography>
            {run && !indeterminate && (
              <Typography variant='caption' sx={{ color: '#42BB93', fontWeight: 700, flexShrink: 0 }}>
                {Math.round(percent)}%
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      {!isActive && latestRun && latestChip && (
        <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap'>
          <Typography variant='caption' sx={{ color: '#98A2AE' }}>
            Last updated{latestWhen ? ` ${latestWhen}` : ''}
            {triggeredByName ? ` by ${triggeredByName}` : ''}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 640,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
};

export function ModalDataFlowRunner(props: Readonly<ModalDataFlowRunnerProps>) {
  const { open, onClose, targetSubjectId } = props;

  const queryClient = useQueryClient();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // The data flow currently being started (before we have a run id).
  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  // The active run being polled.
  const [activeRun, setActiveRun] = useState<{ definitionId: string; runId: string } | null>(null);
  // Run ids whose completion we've already handled, so we don't re-adopt them.
  const handledRunsRef = useRef<Set<string>>(new Set());

  const { data: subjectDetails } = useGetSubjectDetails(open ? targetSubjectId : undefined);
  const { data: definitionsResponse, isFetching } = useGetCubeToSubjectDefinitions(open);
  const { data: tableDefinitionDropdown } = useGetFunctionDefinitionSubjectAttributeDropdown(
    open ? targetSubjectId ?? null : null,
  );
  const { mutate: triggerDefinition } = useTriggerCubeToSubjectDefinition();
  const { data: runResponse } = useGetCubeToSubjectRun(activeRun?.runId, open);
  const run = runResponse?.data;

  const targetSubjectLabel = subjectDetails?.data?.displayName || subjectDetails?.data?.subject || 'this table';

  const definitions: CubeToSubjectDefinition[] = useMemo(
    () =>
      (definitionsResponse?.data ?? []).filter((d) => !targetSubjectId || d.target.subjectId === targetSubjectId),
    [definitionsResponse?.data, targetSubjectId],
  );

  const tableDefinitionLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (tableDefinitionDropdown?.data ?? []).forEach((item) => map.set(item.value.toString(), item.label));
    return map;
  }, [tableDefinitionDropdown?.data]);

  // Resume polling if a definition is already running (currentRunId from the list).
  useEffect(() => {
    if (!open || activeRun || triggeringId) return;
    const running = definitions.find(
      (d) => d.currentRunId && !handledRunsRef.current.has(d.currentRunId),
    );
    if (running?.currentRunId) {
      setActiveRun({ definitionId: running.id, runId: running.currentRunId });
    }
  }, [open, activeRun, triggeringId, definitions]);

  // When the polled run finishes, notify, refresh the grid, and clear.
  useEffect(() => {
    if (!activeRun || !run || !isRunTerminal(run)) return;
    handledRunsRef.current.add(activeRun.runId);
    if (isRunFailed(run)) {
      toast.error(run.errorMessage || 'Data flow update failed');
    } else {
      const executed = run.totalExecuted ?? run.processedMembers ?? 0;
      const excluded = run.totalExcluded ?? 0;
      toast.success(
        `${targetSubjectLabel} updated! ${executed} record${executed === 1 ? '' : 's'} filled` +
          (excluded ? `, ${excluded} skipped` : ''),
      );
      queryClient.invalidateQueries([DISPLAY_TABLE_QUERY_KEY.DATA_LIST]);
    }
    queryClient.invalidateQueries([CUBE_TO_SUBJECT_DEFINITION_QUERY_KEY.LIST]);
    setActiveRun(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, activeRun]);

  // Reset transient run state when the modal closes.
  useEffect(() => {
    if (!open) {
      setActiveRun(null);
      setTriggeringId(null);
      setAdvancedOpen(false);
      handledRunsRef.current.clear();
    }
  }, [open]);

  // A data flow started elsewhere (e.g. another user/session) also blocks new updates.
  const anyDefinitionRunning = useMemo(
    () => definitions.some((d) => d.currentRunId && !handledRunsRef.current.has(d.currentRunId)),
    [definitions],
  );
  // Only one update may run at a time.
  const isBusy = triggeringId !== null || activeRun !== null || anyDefinitionRunning;

  const handleRun = (definitionId: string) => {
    if (isBusy) return;
    setTriggeringId(definitionId);
    triggerDefinition(
      { definitionId },
      {
        onSuccess: (res) => {
          const runId = res?.data?.id;
          if (runId) {
            setActiveRun({ definitionId, runId });
          } else {
            toast.error('Could not start the update');
          }
        },
        onError: () => {
          toast.error('Failed to start the update');
        },
        onSettled: () => {
          setTriggeringId(null);
        },
      },
    );
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Stack sx={modalContentStyle} spacing={2}>
          {/* ── Header ── */}
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1.25}>
              <AccountTreeOutlinedIcon sx={{ color: '#42BB93' }} />
              <Stack spacing={0}>
                <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                  Decision Point
                </Typography>
                <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                  Update {targetSubjectLabel} with the latest calculated figures
                </Typography>
              </Stack>
            </Stack>
            <IconButton size='small' onClick={onClose}>
              <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
            </IconButton>
          </Stack>
          <Divider />

          <Typography variant='body2' sx={{ color: '#475467' }}>
            Press <b>Update</b> on a data flow to pull the latest calculated figures into {targetSubjectLabel}.
          </Typography>

          {isFetching ? (
            <Stack spacing={1}>
              <Skeleton variant='rounded' height={64} />
              <Skeleton variant='rounded' height={64} />
            </Stack>
          ) : definitions.length === 0 ? (
            <Stack alignItems='center' spacing={0.75} sx={{ py: 5 }}>
              <AccountTreeOutlinedIcon sx={{ fontSize: 40, color: '#C8D7EC' }} />
              <Typography variant='body2' fontWeight={600} sx={{ color: '#475467' }}>
                No data flows yet
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                Use Advanced to set up how calculated figures update {targetSubjectLabel}.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {definitions.map((definition) => (
                <DataFlowRunnerRow
                  key={definition.id}
                  definition={definition}
                  targetSubjectLabel={targetSubjectLabel}
                  columnLabel={tableDefinitionLabelById.get(definition.target.tableDefinitionId) || 'a column'}
                  isTriggering={triggeringId === definition.id}
                  runDisabled={isBusy}
                  run={activeRun?.definitionId === definition.id ? run : null}
                  triggeredByName={definition.latestRun?.triggeredBy ?? undefined}
                  onRun={() => handleRun(definition.id)}
                />
              ))}
            </Stack>
          )}

          <Divider />
          {/* ── Footer ── */}
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Button
                size='small'
                variant='main-table-panel-border'
                startIcon={<TuneOutlinedIcon />}
                onClick={() => setAdvancedOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Advanced
              </Button>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                Manage data flow definitions
              </Typography>
            </Stack>
            <Button variant='main-table-panel-border' onClick={onClose} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </Stack>
        </Stack>
      </Modal>

      <ModalDataFlowDefinition
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        targetSubjectId={targetSubjectId}
      />
    </>
  );
}
